import { ContractInsightsData } from '@/pages/ContractDetail';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { format } from 'date-fns';
import { Wallet, TrendingUp, ChevronDown, CalendarDays, FileSignature } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { openSignedUrl } from '@/lib/openSignedUrl';
import { advanceAgreementTarget } from '@/lib/agreement-arc-index';
import { carriesWeekly } from '@/lib/advance-weekly';
import { useAgreementArcIndex } from '@/hooks/useAgreements';

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount).replace('GHS', 'GH₵');

interface ContractInsightLoansProps {
    data: ContractInsightsData;
}

export default function ContractInsightLoans({ data }: ContractInsightLoansProps) {
    const [expandedLoan, setExpandedLoan] = useState<number | null>(null);
    const navigate = useNavigate();
    // An advance signed IN THE APP is read at `/agreement/:documentId`; one signed on paper
    // keeps the scan an admin filed for it. See `advanceAgreementTarget`.
    const arcIndex = useAgreementArcIndex(data.contract?.id ?? null);

    if (!data.loans || data.loans.length === 0) {
        return (
            <div className="border border-dashed rounded-xl p-12 text-center bg-muted/5">
                <div className="h-12 w-12 mx-auto rounded-full bg-muted/20 flex items-center justify-center text-muted-foreground">
                    <Wallet className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-medium mt-4">No Advances</h3>
                <p className="text-sm text-muted-foreground mt-1 mb-6 max-w-xs mx-auto">
                    This contract currently has no active advances.
                </p>
            </div>
        );
    }

    const totalLoanAmount = data.loans.reduce((sum, l) => sum + l.amount, 0);
    const totalRepaid = data.loans.reduce((sum, l) => sum + (l.allocated_total || 0) + (l.repayments_total || 0), 0);
    const totalInterest = data.loans.reduce((sum, l) => sum + (l.amount * (l.interest_rate || 0) / 100), 0);
    const totalOwed = totalLoanAmount + totalInterest;

    return (
        <div className="space-y-8">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <StatCard
                    title="Total Repayable"
                    value={<CurrencyDisplay amount={totalOwed} />}
                    description="The total amount owed across all advances, including both principal and interest charges."
                />
                <StatCard
                    title="Advance"
                    value={<CurrencyDisplay amount={totalLoanAmount} />}
                    description="The combined principal amount of all advances issued under this contract."
                />
                <StatCard
                    title="Interest"
                    value={<CurrencyDisplay amount={totalInterest} />}
                    description="The total interest charges calculated on all advance principals based on their respective interest rates."
                />
                <StatCard
                    title="Repaid"
                    value={<CurrencyDisplay amount={totalRepaid} />}
                    description="The total amount already repaid across all advances, including both direct payments and allocations from contract repayments."
                />
                <StatCard
                    title="Instalment"
                    value={<CurrencyDisplay amount={data.loans.reduce((sum, loan) => {
                        const loanTotal = loan.amount + (loan.amount * (loan.interest_rate || 0) / 100);
                        const repaid = (loan.allocated_total || 0) + (loan.repayments_total || 0);
                        // Use 99.9% threshold to handle floating point precision
                        const isPaidOff = repaid >= loanTotal * 0.999;
                        if (isPaidOff) return sum;
                        const latestInstalment = loan.instalments?.[0]?.amount || 0;
                        return sum + latestInstalment;
                    }, 0)} />}
                    description="The combined weekly instalment amount for all active (unpaid) advances."
                />
            </div>

            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold tracking-tight">Advances</h3>
                </div>

                <div className="space-y-4">
                    {data.loans.map((loan) => {
                        const isExpanded = expandedLoan === loan.id;
                        const loanTotal = loan.amount + (loan.amount * (loan.interest_rate || 0) / 100);
                        const totalRepaid = (loan.allocated_total || 0) + (loan.repayments_total || 0);
                        const repaidPercent = loanTotal > 0 ? Math.min(100, (totalRepaid / loanTotal) * 100) : 0;
                        // Use 99.9% threshold to handle floating point precision issues
                        const isPaidOff = totalRepaid >= loanTotal * 0.999;
                        // Whether this advance is repaid by a weekly of its own, or settled at the
                        // end of the contract. Same question as the premium advance beside it, so
                        // the same answer, in one spelling. See `carriesWeekly`. [D-B58]
                        const hasInstalment = carriesWeekly(loan);
                        const agreementTarget = advanceAgreementTarget(
                            arcIndex.data?.loans.get(loan.id) ?? null,
                            loan.agreement?.agreement_url,
                        );

                        return (
                            <motion.div
                                layout
                                key={loan.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.2 }}
                            >
                                <Card
                                    className={cn(
                                        "overflow-hidden border shadow-sm transition-all duration-200",
                                        isExpanded ? "ring-1 ring-primary/20 shadow-md" : "hover:border-primary/20 hover:shadow-md"
                                    )}
                                >
                                    <div
                                        className="relative cursor-pointer group"
                                        onClick={() => setExpandedLoan(isExpanded ? null : loan.id)}
                                    >
                                        <div className="p-5">
                                            {/* Header Section */}
                                            <div className="flex items-start gap-4 mb-4">
                                                {/* Icon Box */}
                                                <div className={cn(
                                                    "p-3 rounded-2xl transition-colors shrink-0 flex items-center justify-center",
                                                    isPaidOff
                                                        ? "bg-emerald-100/60 text-emerald-700 dark:bg-emerald-900/30"
                                                        : "bg-primary/10 text-primary dark:bg-primary/20"
                                                )}>
                                                    <Wallet className="h-6 w-6" />
                                                </div>

                                                {/* Title & Actions Row */}
                                                <div className="flex-1 min-w-0 pt-0.5">
                                                    <div className="flex items-start justify-between gap-2 mb-1.5">
                                                        {/* WHAT THE ADVANCE IS FOR, BEFORE THE BANK LINE THAT PAID IT.
                                                            `purpose` is `loans.description` — the value the Schedule
                                                            prints as the advance's purpose and the admin dossier makes a
                                                            required field. `description` is the disbursement
                                                            TRANSACTION's narration, which is what this heading has shown
                                                            until now and stays as the fallback: `purpose` is an additive
                                                            insights key and is simply absent on a payload served before
                                                            its migration. The prefix strip belongs to the narration and
                                                            runs either way — a purpose never carries it. */}
                                                        <h4 className="font-semibold text-lg text-foreground truncate">
                                                            {(loan.purpose || loan.description || `Advance #${loan.id}`).replace(/^Loan disbursement:\s*/i, '')}
                                                        </h4>

                                                        {/* Actions Dropdown Chevron */}
                                                        <div className={cn(
                                                            "w-8 h-8 flex items-center justify-center rounded-full transition-transform duration-200 shrink-0 -mt-1 -mr-2 cursor-pointer",
                                                            isExpanded ? "bg-muted rotate-180" : "hover:bg-muted"
                                                        )}>
                                                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                                        <Badge
                                                            variant="secondary"
                                                            className={cn(
                                                                "font-medium px-2.5 py-0.5",
                                                                isPaidOff
                                                                    ? "bg-emerald-100/80 text-emerald-800 hover:bg-emerald-100/80"
                                                                    : "bg-blue-50 text-blue-800 hover:bg-blue-50 border border-blue-100"
                                                            )}
                                                        >
                                                            {isPaidOff ? 'Paid Off' : 'Active'}
                                                        </Badge>
                                                        {/* NO WEEKLY IS AN ARRANGEMENT, NOT AN OVERSIGHT. An
                                                            operational advance taken on a contract whose hire
                                                            purchase agreement leaves no room for a second
                                                            weekly is settled at the end of the contract
                                                            instead: nothing separate is collected while the
                                                            vehicle is being paid for, and once it is paid for
                                                            the contract instalment keeps running until the
                                                            advance is recovered. Amber warned the driver about
                                                            a term of their own contract, and the premium
                                                            advance beside it already says this plainly — so
                                                            this reads the same way, in the same words. [D-B58] */}
                                                        {!hasInstalment && !isPaidOff && (
                                                            <Badge
                                                                variant="outline"
                                                                className="font-medium px-2.5 py-0.5 text-muted-foreground"
                                                            >
                                                                Settled at end of contract
                                                            </Badge>
                                                        )}
                                                    </div>

                                                    {/* Meta details */}
                                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
                                                        <div className="flex items-center gap-1.5">
                                                            <CalendarDays className="h-4 w-4 opacity-70" />
                                                            <span>{format(new Date(loan.lent_at), 'MMM d, yyyy')}</span>
                                                        </div>
                                                        {loan.interest_rate && (
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="w-1 h-1 rounded-full bg-muted-foreground/30 hidden sm:block" />
                                                                <TrendingUp className="h-4 w-4 opacity-70" />
                                                                <span><span className="font-medium text-foreground">{loan.interest_rate}%</span> interest</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Document Buttons */}
                                                    {agreementTarget && (
                                                        <div className="flex flex-wrap items-center gap-2 mt-3">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-8 text-xs font-medium"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (agreementTarget.kind === 'viewer') {
                                                                        navigate(`/agreement/${agreementTarget.documentId}`);
                                                                    } else {
                                                                        void openSignedUrl(agreementTarget.url);
                                                                    }
                                                                }}
                                                            >
                                                                <FileSignature className="h-3.5 w-3.5 mr-1.5" />
                                                                {agreementTarget.kind === 'viewer'
                                                                    ? 'Agreement'
                                                                    : 'Agreement scan'}
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Key Stats Grid */}
                                            <div className="bg-muted/30 rounded-xl p-4 mb-2">
                                                <div className="grid grid-cols-2 gap-y-4 gap-x-6">
                                                    <div>
                                                        <p className="text-xs font-medium text-muted-foreground mb-1 uppercase tracking-wider">Principal</p>
                                                        <p className="text-sm font-semibold text-foreground"><CurrencyDisplay amount={loan.amount} /></p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-medium text-muted-foreground mb-1 uppercase tracking-wider">Total Repayable</p>
                                                        <p className="text-sm font-semibold text-foreground"><CurrencyDisplay amount={loanTotal} /></p>
                                                    </div>
                                                    <div className="col-span-2">
                                                        <div className="flex items-end justify-between mb-1.5">
                                                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Repayment Progress</p>
                                                            <div className="flex items-baseline gap-1.5">
                                                                <p className="text-sm font-bold text-foreground"><CurrencyDisplay amount={totalRepaid} /></p>
                                                                <span className="text-xs font-medium text-muted-foreground">({repaidPercent.toFixed(0)}%)</span>
                                                            </div>
                                                        </div>

                                                        {/* Progress Bar inside Stats Grid */}
                                                        <div className="h-2 bg-background rounded-full overflow-hidden relative border border-border/50">
                                                            {!hasInstalment && !isPaidOff && (
                                                                <div
                                                                    className="absolute inset-0 opacity-40 bg-[length:12px_12px]"
                                                                    style={{
                                                                        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, hsl(var(--muted-foreground)) 4px, hsl(var(--muted-foreground)) 5px)',
                                                                        left: `${repaidPercent}%`,
                                                                        width: `${100 - repaidPercent}%`
                                                                    }}
                                                                />
                                                            )}
                                                            <div
                                                                className={cn("h-full transition-all duration-500 relative z-10",
                                                                    isPaidOff ? "bg-emerald-500" : "bg-primary"
                                                                )}
                                                                style={{ width: `${repaidPercent}%` }}
                                                            />
                                                        </div>
                                                        <div className="flex justify-between mt-1.5">
                                                            <span className="text-xs text-muted-foreground">Repaid: {formatCurrency(totalRepaid)}</span>
                                                            <span className="text-xs text-muted-foreground">Total: {formatCurrency(loanTotal)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded Details */}
                                    <AnimatePresence>
                                        {isExpanded && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <CardContent className="px-5 pb-5 pt-0 border-t bg-muted/5">
                                                    <div className="pt-5">
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                                            <div className="bg-background rounded-lg p-3 border shadow-sm">
                                                                <p className="text-xs text-muted-foreground mb-1">Advance Principal</p>
                                                                <p className="font-semibold text-sm"><CurrencyDisplay amount={loan.amount} /></p>
                                                            </div>
                                                            <div className="bg-background rounded-lg p-3 border shadow-sm">
                                                                <p className="text-xs text-muted-foreground mb-1">Interest Rate</p>
                                                                <p className="font-semibold text-sm flex items-center gap-1">
                                                                    {loan.interest_rate || 0}%
                                                                    <TrendingUp className="h-3 w-3 text-muted-foreground" />
                                                                </p>
                                                            </div>
                                                            <div className="bg-background rounded-lg p-3 border shadow-sm">
                                                                <p className="text-xs text-muted-foreground mb-1">Date Issued</p>
                                                                <p className="font-semibold text-sm">
                                                                    {format(new Date(loan.lent_at), 'dd MMM yyyy')}
                                                                </p>
                                                            </div>
                                                            <div className="bg-background rounded-lg p-3 border shadow-sm">
                                                                <p className="text-xs text-muted-foreground mb-1">Agreement</p>
                                                                <p className="font-semibold text-sm">
                                                                    {loan.agreement?.signed_at
                                                                        ? format(new Date(loan.agreement.signed_at), 'dd MMM yyyy')
                                                                        : 'Not signed'}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className="mb-6">
                                                            <div className="flex items-center justify-between mb-3">
                                                                <h5 className="text-sm font-semibold flex items-center gap-2">
                                                                    <Wallet className="h-4 w-4 text-muted-foreground" />
                                                                    Direct Payments
                                                                </h5>
                                                            </div>
                                                            {loan.repayments && loan.repayments.length > 0 ? (
                                                                <div className="bg-background rounded-xl border divide-y overflow-hidden max-h-[150px] overflow-y-auto">
                                                                    {loan.repayments.map((repayment: any) => (
                                                                        <div key={repayment.id} className="flex justify-between items-center px-4 py-2.5 hover:bg-muted/30 transition-colors group/row">
                                                                            <div className="flex flex-col">
                                                                                <span className="text-sm font-medium">
                                                                                    {format(new Date(repayment.paid_at), 'MMM d, yyyy')}
                                                                                </span>
                                                                                <span className="text-xs text-muted-foreground">
                                                                                    {repayment.description || 'Repayment'}
                                                                                </span>
                                                                            </div>
                                                                            <div className="flex items-center gap-2">
                                                                                <Badge variant="outline" className="text-emerald-600 bg-emerald-50 border-emerald-100 font-mono">
                                                                                    +<CurrencyDisplay amount={repayment.amount} />
                                                                                </Badge>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div className="text-center py-4 border-2 border-dashed rounded-xl">
                                                                    <p className="text-xs text-muted-foreground">No direct payments yet.</p>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Allocation History */}
                                                        <div>
                                                            <div className="flex items-center justify-between mb-3">
                                                                <h5 className="text-sm font-semibold flex items-center gap-2">
                                                                    <Wallet className="h-4 w-4 text-muted-foreground" />
                                                                    Allocation History
                                                                    <span className="text-xs text-muted-foreground font-normal">
                                                                        (from contract repayments)
                                                                    </span>
                                                                </h5>
                                                            </div>
                                                            {loan.recent_allocations && loan.recent_allocations.length > 0 ? (
                                                                <div className="space-y-2">
                                                                    <div className="bg-background rounded-xl border divide-y overflow-hidden">
                                                                        {loan.recent_allocations.slice(0, 5).map((alloc) => (
                                                                            <div key={alloc.repayment_id} className="flex justify-between items-center px-4 py-2.5 hover:bg-muted/30 transition-colors">
                                                                                <div className="flex flex-col">
                                                                                    <span className="text-sm font-medium">
                                                                                        {format(new Date(alloc.payment_date), 'MMM d, yyyy')}
                                                                                    </span>
                                                                                    <span className="text-xs text-muted-foreground">
                                                                                        from <CurrencyDisplay amount={alloc.payment_amount} /> payment
                                                                                    </span>
                                                                                </div>
                                                                                <Badge variant="outline" className="text-blue-600 bg-blue-50 border-blue-100 font-mono">
                                                                                    →<CurrencyDisplay amount={alloc.amount} />
                                                                                </Badge>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="text-center py-8 border-2 border-dashed rounded-xl">
                                                                    <p className="text-sm text-muted-foreground">No allocations from contract repayments yet.</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </Card>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </div >
    );
}
